import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import {
  SIGNATURE_UPLOAD_ACCEPT,
  readSignatureFileAsDataUrl
} from '../../../utils/consentSignatureValidation';
import './SignaturePad.css';

const CANVAS_HEIGHT = 140;

const setupStrokeStyle = (ctx) => {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = '#111827';
};

const SignatureDrawCanvas = ({ value, onChange, hasError = false, disabled = false }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasInkRef = useRef(Boolean(value));
  const lastEmittedValueRef = useRef(value);
  const paintGenerationRef = useRef(0);
  const disabledRef = useRef(disabled);
  const onChangeRef = useRef(onChange);
  const [hasInk, setHasInk] = useState(Boolean(value));

  disabledRef.current = disabled;
  onChangeRef.current = onChange;

  const getPoint = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    const clientY = event.clientY ?? event.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return null;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }, []);

  const paintImageFromValue = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const generation = ++paintGenerationRef.current;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (generation !== paintGenerationRef.current) return;
      const ratio = window.devicePixelRatio || 1;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      setupStrokeStyle(ctx);
    };
    img.src = dataUrl;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const width = Math.max(container.clientWidth, 280);
    const ratio = window.devicePixelRatio || 1;
    const savedValue = hasInkRef.current ? (lastEmittedValueRef.current || value) : null;

    canvas.style.width = `${width}px`;
    canvas.style.height = `${CANVAS_HEIGHT}px`;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(CANVAS_HEIGHT * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    setupStrokeStyle(ctx);

    if (savedValue) {
      paintImageFromValue(savedValue);
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      setupStrokeStyle(ctx);
    }
  }, [value, paintImageFromValue]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    resizeCanvas();
    const observer = new ResizeObserver(() => {
      resizeCanvas();
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [resizeCanvas]);

  useEffect(() => {
    hasInkRef.current = Boolean(value);
    setHasInk(Boolean(value));
    if (value && value !== lastEmittedValueRef.current) {
      lastEmittedValueRef.current = value;
      paintImageFromValue(value);
    }
    if (!value && !isDrawingRef.current) {
      lastEmittedValueRef.current = null;
      paintGenerationRef.current += 1;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const ratio = window.devicePixelRatio || 1;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        setupStrokeStyle(ctx);
      }
    }
  }, [value, paintImageFromValue]);

  const emitChange = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!hasInkRef.current) {
      lastEmittedValueRef.current = null;
      onChangeRef.current?.(null, 'draw');
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    lastEmittedValueRef.current = dataUrl;
    onChangeRef.current?.(dataUrl, 'draw');
  }, []);

  const onDocumentMoveRef = useRef(() => {});
  const onDocumentEndRef = useRef(() => {});

  onDocumentMoveRef.current = (event) => {
    if (!isDrawingRef.current || disabledRef.current) return;
    event.preventDefault();
    const point = getPoint(event);
    const ctx = canvasRef.current?.getContext('2d');
    if (!point || !ctx) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  onDocumentEndRef.current = (event) => {
    if (!isDrawingRef.current) return;
    if (event?.preventDefault) event.preventDefault();
    isDrawingRef.current = false;
    document.removeEventListener('mousemove', onDocumentMove);
    document.removeEventListener('mouseup', onDocumentEnd);
    document.removeEventListener('touchmove', onDocumentMove);
    document.removeEventListener('touchend', onDocumentEnd);
    emitChange();
  };

  const onDocumentMove = useCallback((event) => {
    onDocumentMoveRef.current(event);
  }, []);

  const onDocumentEnd = useCallback((event) => {
    onDocumentEndRef.current(event);
  }, []);

  const startDrawing = useCallback((event) => {
    if (disabledRef.current) return;
    event.preventDefault();
    const point = getPoint(event);
    const ctx = canvasRef.current?.getContext('2d');
    if (!point || !ctx) return;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);

    document.addEventListener('mousemove', onDocumentMove);
    document.addEventListener('mouseup', onDocumentEnd);
    document.addEventListener('touchmove', onDocumentMove, { passive: false });
    document.addEventListener('touchend', onDocumentEnd);
  }, [getPoint, onDocumentEnd, onDocumentMove]);

  useEffect(() => () => {
    document.removeEventListener('mousemove', onDocumentMove);
    document.removeEventListener('mouseup', onDocumentEnd);
    document.removeEventListener('touchmove', onDocumentMove);
    document.removeEventListener('touchend', onDocumentEnd);
  }, [onDocumentEnd, onDocumentMove]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const container = containerRef.current;
    if (!canvas || !ctx || !container) return;
    const width = Math.max(container.clientWidth, 280);
    ctx.clearRect(0, 0, width, CANVAS_HEIGHT);
    hasInkRef.current = false;
    setHasInk(false);
    lastEmittedValueRef.current = null;
    onChangeRef.current?.(null, 'draw');
  }, []);

  return (
    <div
      ref={containerRef}
      className={`signature-pad ${hasError ? 'is-invalid' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        onMouseDown={startDrawing}
        onTouchStart={startDrawing}
        aria-label="Patient signature drawing area"
      />
      {!hasInk && !disabled && (
        <span className="signature-pad-placeholder">Sign here with your mouse or finger</span>
      )}
      <button
        type="button"
        className="signature-pad-clear-btn"
        onClick={handleClear}
        disabled={disabled || !hasInk}
      >
        Clear
      </button>
    </div>
  );
};

const SignatureUpload = ({
  value,
  onChange,
  hasError = false,
  disabled = false,
  uploadError,
  onUploadError
}) => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (!value) {
      setFileName('');
    }
  }, [value]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const dataUrl = await readSignatureFileAsDataUrl(file);
      setFileName(file.name);
      onUploadError?.(null);
      onChange?.(dataUrl, 'upload');
    } catch (error) {
      setFileName('');
      onChange?.(null, 'upload');
      onUploadError?.(error.message || 'Failed to upload signature image.');
    }
  };

  const handleClear = () => {
    setFileName('');
    onUploadError?.(null);
    onChange?.(null, 'upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`signature-upload ${hasError ? 'is-invalid' : ''} ${disabled ? 'is-disabled' : ''}`}>
      {value ? (
        <div className="signature-upload-preview-wrap">
          <img src={value} alt="Uploaded patient signature" className="signature-upload-preview" />
          {fileName && <span className="signature-upload-filename">{fileName}</span>}
          <button
            type="button"
            className="signature-pad-clear-btn"
            onClick={handleClear}
            disabled={disabled}
          >
            Remove
          </button>
        </div>
      ) : (
        <label className={`signature-upload-dropzone ${disabled ? 'is-disabled' : ''}`}>
          <Upload size={28} className="signature-upload-icon" />
          <span className="signature-upload-label">Click to upload or drag and drop</span>
          <small className="signature-upload-hint">JPG or PNG (max. 10 MB)</small>
          <input
            ref={fileInputRef}
            type="file"
            accept={SIGNATURE_UPLOAD_ACCEPT}
            className="signature-upload-input"
            disabled={disabled}
            onChange={handleFileChange}
          />
        </label>
      )}
      {uploadError && (
        <span className="text-red-500 form-field-error signature-upload-error" role="alert">
          {uploadError}
        </span>
      )}
    </div>
  );
};

const SignaturePad = ({
  value,
  onChange,
  hasError = false,
  disabled = false,
  signatureSource = 'draw',
  onSignatureSourceChange
}) => {
  const initialSource = signatureSource === 'upload' ? 'upload' : 'draw';
  const [mode, setMode] = useState(initialSource);
  const [drawnValue, setDrawnValue] = useState(initialSource === 'draw' ? (value || null) : null);
  const [uploadedValue, setUploadedValue] = useState(initialSource === 'upload' ? (value || null) : null);
  const [uploadError, setUploadError] = useState(null);
  const lastExternalKeyRef = useRef(`${initialSource}::${value || ''}`);

  useEffect(() => {
    const nextSource = signatureSource === 'upload' ? 'upload' : 'draw';
    const nextKey = `${nextSource}::${value || ''}`;
    if (lastExternalKeyRef.current === nextKey) return;
    lastExternalKeyRef.current = nextKey;
    setMode(nextSource);
    if (nextSource === 'upload') {
      setUploadedValue(value || null);
      setDrawnValue(null);
    } else {
      setDrawnValue(value || null);
      setUploadedValue(null);
    }
  }, [value, signatureSource]);

  const commitSignature = (dataUrl, source) => {
    lastExternalKeyRef.current = `${source}::${dataUrl || ''}`;
    if (source === 'draw') {
      setDrawnValue(dataUrl || null);
      setUploadedValue(null);
    } else {
      setUploadedValue(dataUrl || null);
      setDrawnValue(null);
    }
    if (dataUrl) {
      setUploadError(null);
    }
    onChange?.(dataUrl, source);
    onSignatureSourceChange?.(source);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setUploadError(null);
  };

  return (
    <div className="signature-field">
      <div className="signature-mode-tabs" role="tablist" aria-label="Signature input method">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'draw'}
          className={`signature-mode-tab ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => handleModeChange('draw')}
        >
          Draw Signature
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'upload'}
          className={`signature-mode-tab ${mode === 'upload' ? 'active' : ''}`}
          onClick={() => handleModeChange('upload')}
        >
          Upload Signature
        </button>
      </div>

      {mode === 'draw' ? (
        <SignatureDrawCanvas
          value={drawnValue}
          onChange={commitSignature}
          hasError={hasError}
          disabled={disabled}
        />
      ) : (
        <SignatureUpload
          value={uploadedValue}
          onChange={commitSignature}
          hasError={hasError}
          disabled={disabled}
          uploadError={uploadError}
          onUploadError={setUploadError}
        />
      )}
    </div>
  );
};

export default SignaturePad;
